// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
// Disabling import sort because it breaks the app
// eslint-disable-next-line simple-import-sort/imports
import { createApp, reactive } from 'vue';
import { Multiselect } from 'vue-multiselect';
import ECharts from 'vue-echarts';
import { registerTransform, registerTheme, use } from 'echarts/core';
import { BarChart, CandlestickChart, LineChart, PieChart } from 'echarts/charts';
import {
  BrushComponent,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { transform } from 'echarts-stat';

import numbro from 'numbro';
import deDE from 'numbro/dist/languages/de-DE.min.js';
import enGB from 'numbro/dist/languages/en-GB.min.js';
import enAU from 'numbro/dist/languages/en-AU.min.js';
import esES from 'numbro/dist/languages/es-ES.min.js';
import frFR from 'numbro/dist/languages/fr-FR.min.js';
import jaJP from 'numbro/dist/languages/ja-JP.min.js';
import zhCN from 'numbro/dist/languages/zh-CN.min.js';

import mapboxgl from 'mapbox-gl';
import { RecycleScroller } from 'vue-virtual-scroller';
import BaiduMap from 'vue-baidu-map-3x';
import * as Sentry from '@sentry/vue';
import VueGtag from 'vue-gtag';

import LocalCache from '@/utils/LocalCache';
import { Chart } from '@/common/components';
import clickOutsideDirective from '@/common/directives/clickOutside.directive';
import { draggable, dropper } from '@/common/directives/dragReorder.directive';
import sanitizeHtml from '@/common/directives/sanitizeHtml.directive';
import visibility from '@/common/directives/visibility.directive';
import rootStore from '@/store';
import i18n, { loadLanguageAsync, numberFormats } from '@/vueI18n';
import ValidatorMixins from '@/validator/mixin';

import version from '@/version.json';
import router from '@/router';
import App from '@/App.vue';
import { EntityActions, Modal, ToggleButton } from '@/common/components/controls';
import { Breadcrumbs } from '@/org-app/components/controls';

import theme from '@/common/chart-themes/chartTheme.json';
import themeBids from '@/common/chart-themes/chartThemeBids.json';
import themeEightTones from '@/common/chart-themes/chartThemeEightTones.json';
import themeTwoTones from '@/common/chart-themes/chartThemeTwoTones.json';
import themeThreeTones from '@/common/chart-themes/chartThemeThreeTones.json';
import themeTenTones from '@/common/chart-themes/chartThemeTenTones.json';
import themeTenTonesSitesNetwork from '@/common/chart-themes/chartThemeTenTonesSitesNetworkPane.json';
import themeOneToneBlue from '@/common/chart-themes/chartThemeOneToneBlue.json';
import themeOneToneRed from '@/common/chart-themes/chartThemeOneToneRed.json';
import themeOneToneGreen from '@/common/chart-themes/chartThemeOneToneGreen.json';
import themeOneToneYellow from '@/common/chart-themes/chartThemeOneToneYellow.json';
import themePlays from '@/common/chart-themes/chartThemePlays.json';
import themeHistoricalBids from '@/common/chart-themes/chartThemeHistoricalBids.json';
import themeHourlyBids from '@/common/chart-themes/chartThemeHourlyBids.json';

import { selectTheme } from '@/theming';
import '@/styles/styles.scss';
import '@/common/components/tooltips/tooltip.scss';
import { MapboxPublicToken, environment, BaiduMapsApiKey, setAppInstance, setStore } from '@/globals';
import languages from '@/lang';
import Validator from '@/validator/Validator';
import tooltipDirective from '@/common/components/tooltips/tooltipDirective';
import NumbroUtils from './utils/NumbroUtils';
import { VueQueryPlugin } from '@tanstack/vue-query';
import queryClient from '@/org-app/services/tanstack/queryUtils';
import { datadogRum } from '@datadog/browser-rum';
import Environment from '@/common/enums/environment.enum';

//
// Validation
//
// const validationDictionary = ValidationAttributes.computed.dictionary();

//
// Echarts imports
//
use([
  BarChart,
  BrushComponent,
  CandlestickChart,
  CanvasRenderer,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  PieChart,
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  VisualMapComponent,
]);

registerTransform(transform.regression);

//
// Echart theme
//
registerTheme('ayudax', theme);
registerTheme('ayudax-bids', themeBids);
registerTheme('ayudax-two-tones', themeTwoTones);
registerTheme('ayudax-three-tones', themeThreeTones);
registerTheme('ayudax-ten-tones', themeTenTones);
registerTheme('ayudax-one-tone-blue', themeOneToneBlue);
registerTheme('ayudax-one-tone-red', themeOneToneRed);
registerTheme('ayudax-one-tone-green', themeOneToneGreen);
registerTheme('ayudax-one-tone-yellow', themeOneToneYellow);
registerTheme('ayudax-plays', themePlays);
registerTheme('ayudax-eight-tones', themeEightTones);
registerTheme('hs-historical-bids', themeHistoricalBids);
registerTheme('hs-hourly-bids', themeHourlyBids);
registerTheme('ten-tones-sites-network-pane', themeTenTonesSitesNetwork);

//
// MapBox
//
mapboxgl.accessToken = MapboxPublicToken;

numbro.registerLanguage(enGB);
numbro.registerLanguage(enAU);
numbro.registerLanguage(deDE);
numbro.registerLanguage(frFR);
numbro.registerLanguage(esES);
numbro.registerLanguage(jaJP);
numbro.registerLanguage(zhCN);

// ----------------------------------------------------------------------------
// Override currency symbol for all locales.
// ----------------------------------------------------------------------------
let defaultCurrencySymbol: string | null = null;
let defaultCurrency = 'EUR';
const org = LocalCache.get('organization');
if (org != null) {
  if (org.currency_symbol != null) {
    defaultCurrencySymbol = org.currency_symbol;
  }
  if (org.currency != null) {
    defaultCurrency = org.currency;
  }
}
const orgSettings = LocalCache.get('organizationSettings');
if (!defaultCurrencySymbol && orgSettings != null) {
  if (orgSettings.currencySymbol != null) {
    defaultCurrencySymbol = orgSettings.currencySymbol;
  }
}

if (!defaultCurrencySymbol) defaultCurrencySymbol = '€';

NumbroUtils.setCurrencySymbol(defaultCurrencySymbol);

Object.keys(numberFormats).forEach((x) => {
  const currencyFormat = numberFormats[x].currency || 'EUR';
  currencyFormat.currency = defaultCurrency;
  i18n.global.mergeNumberFormat(x, { currency: currencyFormat });
});

// ----------------------------------------------------------------------------

const getDefaultLanguage = () => {
  if (window == null || window.navigator == null) {
    return 'en-US';
  }
  let lang = window.navigator.languages != null ? window.navigator.languages[0] : window.navigator.language;
  if (lang == null || !languages.find((x) => x.value === lang)) {
    lang = 'en-US';
  }
  return lang;
};

let lang = window.localStorage != null && LocalCache.get('lang') != null ? LocalCache.get('lang').value : null;
if (lang == null || !languages.find((x) => x.value === lang)) {
  lang = getDefaultLanguage();
}

loadLanguageAsync(lang).then(() => {
  window.environment = environment;

  // This let's us access VueX through a chrome extension.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.store = rootStore;

  async function setUp() {
    await selectTheme();

    if (environment === Environment.DEV || environment === Environment.STAGING) {
      datadogRum.init({
        applicationId: 'c2a6f530-53ef-4943-a526-9c4b1c3cb9b9',
        clientToken: 'pubd1c09e2da5c4055c49fd7eeb20d0e7b9',
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service: 'perion-dooh-web-app',
        env: 'staging',
        // Specify a version number to identify the deployed version of your application in Datadog
        // version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        defaultPrivacyLevel: 'mask-user-input',
      });
    }

    const app = createApp({
      router,
      store: rootStore,
      http: {},
      components: { App },
      mixins: [ValidatorMixins],
      template: '<App/>',
      renderError(h, err) {
        return h('pre', { style: { color: 'red' } }, err.stack);
      },
      // setup() {
      //   const { t } = useI18n(); // call `useI18n`, and spread `t` from  `useI18n` returning
      //   return { t }; // return render context that included `t`
      // },
    });

    setStore(rootStore);
    setAppInstance(app);

    app.config.globalProperties.currencySymbol = defaultCurrencySymbol;

    const validator = new Validator(app);
    app.config.globalProperties.$validator = reactive(validator);
    app.provide('validator', validator);

    app.use(VueQueryPlugin, { queryClient });
    app.use(i18n);
    app.use(rootStore);
    app.use(router);

    app.use(BaiduMap, {
      ak: BaiduMapsApiKey,
    });

    app.component('ToggleButton', ToggleButton);

    let GA4Tag = 'G-BYRQP93CG0';
    if (window.location.host.indexOf('uat') !== -1) {
      GA4Tag = 'G-3HN4NKTL6R';
    } else if (window.location.host.indexOf('apps') !== -1) {
      GA4Tag = 'G-JR6YCHWFFG';
    }

    app.use(VueGtag, {
      config: { id: GA4Tag },
    });

    //
    // Directives
    //
    app.directive('tooltip', tooltipDirective);
    app.directive('click-outside', clickOutsideDirective);
    app.directive('draggable', draggable);
    app.directive('dropper', dropper);
    app.directive('sanitize-html', sanitizeHtml);
    app.directive('observe-visibility', visibility);

    //
    // Global components
    //
    app.component('Multiselect', Multiselect);
    app.component('Modal', Modal);
    app.component('Breadcrumbs', Breadcrumbs);
    app.component('VueEcharts', ECharts);
    app.component('RecycleScroller', RecycleScroller);
    app.component('EntityActions', EntityActions);
    app.component('Chart', Chart);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    app.config.globalProperties.lang = lang;

    //
    // Sentry
    //
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (environment !== 'dev') {
      Sentry.init({
        app,
        dsn: 'https://3ecc4b01eed043e1b6332446a7162191@sentry.io/1336362',
        tracePropagationTargets: ['localhost', 'hivestack.com', 'liveboard.co.jp', /^\//],
        release: version.number,
        environment,
        tracesSampleRate: 1.0,
      });
    }

    app.provide('config', app.config);

    app.mount('#app');
  }

  setUp();
});
